import types from '../mutation-types';
import CarCatalogAPI from '../../api/wgpt/carCatalog';
import { throwErrorMessage } from '../utils/api';

export const state = {
  catalog: {
    makes: [],
    colors: [],
  },
  modelsByMakeId: {},
  trimsByModelId: {},
  uiFlags: {
    isFetching: false,
    isFetchingModels: false,
    isFetchingTrims: false,
    isCreating: false,
    isSavingAlias: false,
  },
};

export const getters = {
  getCatalog: $state => $state.catalog,
  getUIFlags: $state => $state.uiFlags,
  getModels: $state => makeId => $state.modelsByMakeId[makeId] || [],
  getTrims: $state => modelId => $state.trimsByModelId[modelId] || [],
};

export const actions = {
  get: async ({ commit, state: $state }) => {
    if ($state.catalog.makes.length) return;
    commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isFetching: true });
    try {
      const response = await CarCatalogAPI.get();
      commit(types.SET_WGPT_CAR_CATALOG, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isFetching: false });
    }
  },
  getModels: async ({ commit, state: $state }, makeId) => {
    if (!makeId || $state.modelsByMakeId[makeId]) return;
    commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isFetchingModels: true });
    try {
      const response = await CarCatalogAPI.getModels(makeId);
      commit(types.SET_WGPT_CAR_CATALOG_MODELS, {
        makeId,
        models: response.data,
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isFetchingModels: false });
    }
  },
  getTrims: async ({ commit, state: $state }, modelId) => {
    if (!modelId || $state.trimsByModelId[modelId]) return;
    commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isFetchingTrims: true });
    try {
      const response = await CarCatalogAPI.getTrims(modelId);
      commit(types.SET_WGPT_CAR_CATALOG_TRIMS, {
        modelId,
        trims: response.data,
      });
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isFetchingTrims: false });
    }
  },
  createEntry: async ({ commit }, { type, ...payload }) => {
    const apiCalls = {
      make: CarCatalogAPI.createMake,
      model: CarCatalogAPI.createModel,
      trim: CarCatalogAPI.createTrim,
      color: CarCatalogAPI.createColor,
    };
    commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isCreating: true });
    try {
      const response = await apiCalls[type].call(CarCatalogAPI, payload);
      commit(types.ADD_WGPT_CAR_CATALOG_ITEM, { type, item: response.data });
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isCreating: false });
    }
    return null;
  },
  createAlias: async ({ commit }, { type, entityId, name, locale }) => {
    commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isSavingAlias: true });
    try {
      const response = await CarCatalogAPI.createAlias({
        entity_type: type,
        entity_id: entityId,
        name,
        locale,
      });
      commit(types.UPDATE_WGPT_CAR_CATALOG_ITEM, { type, item: response.data });
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isSavingAlias: false });
    }
    return null;
  },
  deleteAlias: async ({ commit }, { type, aliasId }) => {
    commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isSavingAlias: true });
    try {
      const response = await CarCatalogAPI.deleteAlias(aliasId);
      commit(types.UPDATE_WGPT_CAR_CATALOG_ITEM, { type, item: response.data });
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CAR_CATALOG_UI_FLAG, { isSavingAlias: false });
    }
    return null;
  },
};

const sortByName = items => items.sort((a, b) => a.name.localeCompare(b.name));

const collectionFor = ($state, type, item) => {
  switch (type) {
    case 'make':
      return $state.catalog.makes;
    case 'color':
      return $state.catalog.colors;
    case 'model':
      return $state.modelsByMakeId[item.wgpt_car_make_id];
    case 'trim':
      return $state.trimsByModelId[item.wgpt_car_model_id];
    default:
      return null;
  }
};

export const mutations = {
  [types.SET_WGPT_CAR_CATALOG_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_WGPT_CAR_CATALOG]($state, data) {
    $state.catalog = data;
  },
  [types.SET_WGPT_CAR_CATALOG_MODELS]($state, { makeId, models }) {
    $state.modelsByMakeId = { ...$state.modelsByMakeId, [makeId]: models };
  },
  [types.SET_WGPT_CAR_CATALOG_TRIMS]($state, { modelId, trims }) {
    $state.trimsByModelId = { ...$state.trimsByModelId, [modelId]: trims };
  },
  [types.ADD_WGPT_CAR_CATALOG_ITEM]($state, { type, item }) {
    if (type === 'model' && !$state.modelsByMakeId[item.wgpt_car_make_id]) {
      $state.modelsByMakeId[item.wgpt_car_make_id] = [];
    }
    if (type === 'trim' && !$state.trimsByModelId[item.wgpt_car_model_id]) {
      $state.trimsByModelId[item.wgpt_car_model_id] = [];
    }
    const collection = collectionFor($state, type, item);
    if (!collection) return;
    collection.push(item);
    sortByName(collection);
  },
  [types.UPDATE_WGPT_CAR_CATALOG_ITEM]($state, { type, item }) {
    const collection = collectionFor($state, type, item);
    if (!collection) return;
    const index = collection.findIndex(record => record.id === item.id);
    if (index > -1) collection.splice(index, 1, item);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
